/* eslint-disable react-hooks/exhaustive-deps */
/**
 * B100100 倉庫マスタメンテ
 */
import {
  B100100Create,
  B100100Soko,
  B100100SokoshubetsuKbn,
  B100100Update,
  catchRestError,
} from "clients/rest";
import {
  msgState,
  SCodeSelect,
  SConfirmDialog,
  SFileButton,
  SFrame,
  SLabel,
  SNormalButton,
  SNumberField,
  SText,
  STextField,
} from "components";
import { getClientMessage } from "messages";
import {
  B100100Mode,
  B100100Sokoshubetsu,
  getSCode,
  LocationKanriKbn,
  SakujoyoteiKbn,
  SCode,
} from "messages/codes";
import { labels } from "messages/labels";
import { B000190, B100910 } from "pages";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { sokoshubetsuKbn } from "services/codes";
import { focusState, useFocus } from "states";

// 呼び出し時のプロパティ
type Props = Partial<{
  // 呼び出し元へデータを送る場合に設定する。
  handleSubmit: (data: any) => void;
}>;

// コンポーネントで設定されるフィールドの型を定義する
type Fields = Partial<{
  modeKbn: SCode;
  sokoCd: string;
  sokoNm: string;
  sokoKana: string;
  sokoNmr: string;
  sokoshubetsuKbn: SCode;
  logicalsokoCd: string;
  logicalsokoNmr: string;
  physicalsokoCd: string;
  physicalsokoNmr: string;
  locationkanriKbn: SCode;
  sakujoyoteiKbn: SCode;
  version: number;
  dirtyFields: {
    sokoCd: string;
  };
}>;

// フィールドの初期値を設定する。
const defaultFields: Fields = {
  modeKbn: getSCode(B100100Mode, "0"),
  sokoCd: "",
  sokoNm: "",
  sokoKana: "",
  sokoNmr: "",
  sokoshubetsuKbn: getSCode(B100100Sokoshubetsu, "2"),
  logicalsokoCd: "",
  logicalsokoNmr: "",
  physicalsokoCd: "",
  physicalsokoNmr: "",
  locationkanriKbn: getSCode(LocationKanriKbn, "0"),
  sakujoyoteiKbn: getSCode(SakujoyoteiKbn, "0"),
  version: undefined,
  dirtyFields: {
    sokoCd: "",
  },
};

const focusId = "B100100";

// モード(新規)
const modeNew = "0";
// モード(修正)
const modeEdit = "1";

// B100010 倉庫マスタメンテ
export const B100100 = (props: Props) => {
  useFocus(focusId);
  return <Page {...props} />;
};

const Page = (props: Props) => {
  // 状態管理の初期設定
  // フォーム関数
  const methods = useForm<Fields>({
    mode: "onBlur",
    defaultValues: defaultFields,
  });
  // dirtyFields初期化
  const resetDirtyFields = () => {
    const fields = methods.getValues();
    methods.setValue("dirtyFields.sokoCd", fields.sokoCd ?? "");
  };

  // フォーカス設定
  const { focus, register } = useRecoilValue(focusState(focusId));
  // エラー表示
  const setMsg = useSetRecoilState(msgState);
  // 画面遷移
  const navigate = useNavigate();
  // 再レンダリング用フラグ
  const [reRender, setReRender] = useState(false);
  const reRendering = () => {
    resetDirtyFields();
    setReRender(!reRender);
  };

  // 活性設定
  // 新規モードかどうか
  const isNew = () => methods.getValues().modeKbn?.value === modeNew;
  // 倉庫種別区分が1:論理倉庫かどうか
  const isRonri = () =>
    Number(methods.getValues().sokoshubetsuKbn?.value) ===
    sokoshubetsuKbn.logicalSoko;
  // 倉庫種別区分が2:引当倉庫かどうか
  const isHikiate = () =>
    Number(methods.getValues().sokoshubetsuKbn?.value) ===
    sokoshubetsuKbn.hikiateSoko;

  // 初期表示イベント
  useEffect(() => {
    (async () => {
      // 倉庫種別関連設定
      await B100100SokoshubetsuKbn(methods, navigate, setMsg);
      reRendering();
    })();

    // 初期フォーカス
    focus(1);
  }, []);

  // モード変更時
  const handleChangeModeKbn = () => {
    // 新規の場合
    if (!isNew()) {
      methods.reset();
      methods.setValue("modeKbn", getSCode(B100100Mode, modeEdit));
    }
    // 修正の場合
    else {
      methods.reset();
    }

    (async () => {
      // 倉庫種別関連設定
      await B100100SokoshubetsuKbn(methods, navigate, setMsg);
      reRendering();
    })();
  };
  // 更新
  const handleClickUpdateButton = () => {
    // 新規の場合
    if (isNew()) {
      B100100Create(methods)
        .then(() => {
          methods.reset();
          (async () => {
            // 倉庫種別関連設定
            await B100100SokoshubetsuKbn(methods, navigate, setMsg);
            reRendering();

            // ↓これがあると完了メッセージが表示される
            methods.setValue("sokoCd", "");
            setMsg(getClientMessage("B100100_001"));
          })();
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    }
    // 修正の場合
    else {
      B100100Update(methods)
        .then(() => {
          (async () => {
            methods.reset();
            // 倉庫種別関連設定
            await B100100SokoshubetsuKbn(methods, navigate, setMsg);
            reRendering();

            // ↓これがあると完了メッセージが表示される
            methods.setValue("sokoCd", "");
            setMsg(getClientMessage("B100100_002"));
          })();
        })
        .catch((reason) => {
          catchRestError(reason, navigate, setMsg);
        });
    }
  };

  // 倉庫コード変更
  const handleChangeSokoCd = () => {
    const fields = methods.getValues();
    if (fields.sokoCd === fields.dirtyFields?.sokoCd) return;

    (async () => {
      // 倉庫情報表示
      await B100100Soko(methods, navigate, setMsg);
      reRendering();
    })();
  };

  // 倉庫種別区分変更
  const handleChangeSokoshubetsuKbn = () => {
    (async () => {
      // 倉庫種別関連設定
      await B100100SokoshubetsuKbn(methods, navigate, setMsg);
      reRendering();
    })();
  };

  // レイアウト設定
  return (
    <>
      <FormProvider {...methods}>
        <SFrame
          title="倉庫マスタメンテ"
          gridTemplateColumns="11rem 4rem  4rem 5rem 4rem 4rem 13rem 3rem 4rem 11rem 5rem "
        >
          <SFileButton
            label={labels.buttons.excel}
            align="right"
            gridColumn="11"
            {...register(19)}
            dialog={<B100910 gamenId="100100" />}
            dialogAction="close"
          />
          <SLabel required gridColumn="1">
            モード
          </SLabel>
          <SCodeSelect
            name="modeKbn"
            label="モード"
            required
            gridColumn="span 2"
            {...register(1)}
            options={B100100Mode}
            onChange={() => handleChangeModeKbn()}
          />
          <SLabel required gridColumn="1">
            倉庫コード
          </SLabel>

          {isNew() ? (
            <>
              <SNumberField
                name="sokoCd"
                label="倉庫コード"
                {...register(2)}
                code
                required
                size={3}
              />
            </>
          ) : (
            <>
              <SNumberField
                name="sokoCd"
                label="倉庫コード"
                {...register(2)}
                code
                required
                size={3}
                onBlur={handleChangeSokoCd}
                dialog={<B000190 deljogaiKbn="1" />}
                handleSubmit={(data: any) => {
                  methods.setValue("sokoCd", data.sokoCd);
                  methods.setValue("sokoNm", data.sokoNm);
                  methods.setValue("sokoKana", data.sokoKana);
                  methods.setValue("sokoNmr", data.sokoNmr);
                  methods.setValue(
                    "sokoshubetsuKbn",
                    getSCode(B100100Sokoshubetsu, data.sokoshubetsuKbn)
                  );
                  methods.setValue(
                    "sakujoyoteiKbn",
                    getSCode(SakujoyoteiKbn, data.sakujoyoteiKbn)
                  );
                  methods.setValue("version", data.version);
                  (async () => {
                    // 倉庫種別関連設定
                    await B100100SokoshubetsuKbn(methods, navigate, setMsg);
                    reRendering();
                  })();
                }}
              />
            </>
          )}

          <SLabel required gridColumn="1">
            倉庫名
          </SLabel>
          <STextField
            name="sokoNm"
            label="倉庫名"
            gridColumn="span 3"
            required
            size={20}
            {...register(3)}
          />
          <SText></SText>
          <SLabel>カナ名</SLabel>
          <STextField
            name="sokoKana"
            label="カナ名"
            size={20}
            {...register(4)}
          />
          <SText></SText>
          <SLabel required>略名</SLabel>
          <STextField
            name="sokoNmr"
            required
            label="略名"
            size={10}
            {...register(5)}
          />
          <SLabel required gridColumn="1">
            倉庫種別
          </SLabel>
          <SCodeSelect
            name="sokoshubetsuKbn"
            label="倉庫種別"
            required
            gridColumn="span 2"
            {...register(6)}
            options={B100100Sokoshubetsu}
            onChange={() => handleChangeSokoshubetsuKbn()}
          />

          {isHikiate() && (
            <>
              <SLabel gridColumn="1">論理倉庫コード</SLabel>
              <SNumberField
                name="logicalsokoCd"
                label="論理倉庫コード"
                code
                disabled
              />
              <STextField
                name="logicalsokoNmr"
                label="論理倉庫名"
                gridColumn="span 3"
                disabled
              />
            </>
          )}

          {isRonri() && (
            <>
              <SLabel gridColumn="1">物理倉庫コード</SLabel>
              <SNumberField
                name="physicalsokoCd"
                label="物理倉庫コード"
                code
                disabled
              />
              <STextField
                name="physicalsokoNmr"
                label="物理倉庫名"
                gridColumn="span 3"
                disabled
              />
            </>
          )}

          <SLabel required gridColumn="1">
            ロケーション管理区分
          </SLabel>
          <SCodeSelect
            name="locationkanriKbn"
            label="ロケーション管理区分"
            required
            gridColumn="span 2"
            options={LocationKanriKbn}
            disabled
          />
          <SLabel gridColumn="1">削除予定区分</SLabel>
          <SCodeSelect
            name="sakujoyoteiKbn"
            label="削除予定区分"
            gridColumn="span 2"
            {...register(7)}
            options={SakujoyoteiKbn}
          />
          <SNormalButton
            label={labels.buttons.update}
            gridRow="9"
            gridColumn="11"
            align="right"
            {...register(8)}
            dialog={
              <SConfirmDialog
                title="マスタ更新"
                msg="更新します。よろしいですか？"
              />
            }
            dialogAction="confirm"
            handleSubmit={handleClickUpdateButton}
          />
        </SFrame>
      </FormProvider>
    </>
  );
};
